import { ArrowBack, PlayArrow, UploadFile } from "@mui/icons-material";
import { Alert, Autocomplete, Button, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { RiskCompanyDTO, RiskSheetDTO } from "../../types/RiskAssessmentDTO";
import RiskMissingPositionsAlert from "./RiskMissingPositionsAlert";
import RiskSpinner from "./RiskSpinner";
import { POSITION } from "./riskColumns";
import { companyOptionLabel, filterCompanies } from "./riskEditorState";
import './RiskSelectionStep.scss';

interface Props {
	companies: RiskCompanyDTO[];
	company: RiskCompanyDTO | null;
	onCompanyChange: (company: RiskCompanyDTO | null) => void;
	file: File | null;
	fileInputKey: number;
	onFileChange: (file: File | null) => void;
	parsing: boolean;
	parseError: string;
	sheet: RiskSheetDTO | null;
	assessmentDate: Dayjs | null;
	onAssessmentDateChange: (value: Dayjs | null) => void;
	validityDate: Dayjs | null;
	onValidityDateChange: (value: Dayjs | null) => void;
	validityError: boolean;
	canStart: boolean;
	starting: boolean;
	onStart: () => void;
}

const RiskSelectionStep: FC<Props> = ({ companies, company, onCompanyChange, file, fileInputKey, onFileChange,
		parsing, parseError, sheet, assessmentDate, onAssessmentDateChange, validityDate, onValidityDateChange,
		validityError, canStart, starting, onStart }) => {
	const positions = sheet ? new Set(sheet.rows.map(row => row.values[POSITION])).size : 0;
	const navigate = useNavigate();
	return (
		<div className="risk-selection">
			<div className="cabecalho">
				<Typography variant="h6">Nova avaliação de risco</Typography>
				<Button variant="outlined" startIcon={<ArrowBack/>} onClick={() => navigate('/avaliacao-riscos')}>
					Voltar ao histórico
				</Button>
			</div>
			<div className="cartao">
				<div className="campos">
					<Autocomplete
						options={companies}
						value={company}
						onChange={(_, next) => onCompanyChange(next)}
						getOptionLabel={companyOptionLabel}
						isOptionEqualToValue={(option, value) => option.companyId === value.companyId}
						filterOptions={(options, state) => filterCompanies(options, state.inputValue)}
						renderOption={(props, option) => (
							<li {...props} key={option.companyId}>
								<div className="opcao-empresa">
									<span>{companyOptionLabel(option)}</span>
									{!!option.cnpj && <small>{option.cnpj}</small>}
								</div>
							</li>
						)}
						noOptionsText="Nenhuma empresa encontrada"
						renderInput={(params) => (
							<TextField {...params} required label="Empresa" placeholder="Nome, código ou CNPJ" size="small"/>
						)}
						sx={{ width: { xs: '100%', md: '520px' } }}
					/>
					<div className="arquivo">
						<Button
							variant="outlined"
							component="label"
							startIcon={parsing ? <RiskSpinner size={18}/> : <UploadFile/>}
							disabled={parsing || starting}
						>
							Selecionar planilha de origem
							<input
								key={fileInputKey}
								hidden
								type="file"
								accept=".xlsx"
								onChange={(event) => {
									const selected = event.target.files?.[0] || null;
									event.target.value = '';
									onFileChange(selected);
								}}
							/>
						</Button>
						<span className="nome-arquivo">{file ? file.name : 'Nenhum arquivo selecionado'}</span>
					</div>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<div className="datas">
							<DatePicker
								label="Data da nova avaliação"
								value={assessmentDate}
								onChange={(value) => onAssessmentDateChange(value)}
								slotProps={{ textField: { size: 'small', required: true } }}
							/>
							<DatePicker
								label="Validade da nova avaliação"
								value={validityDate}
								onChange={(value) => onValidityDateChange(value)}
								minDate={assessmentDate ? assessmentDate.add(1, 'day') : undefined}
								slotProps={{
									textField: {
										size: 'small',
										required: true,
										error: validityError,
										helperText: validityError ? 'A validade deve ser posterior à data da avaliação' : undefined
									}
								}}
							/>
						</div>
					</LocalizationProvider>
				</div>
				<div className="situacao">
					{parsing && (
						<div className="carregando">
							<RiskSpinner size={22}/>
							<span>Lendo a planilha...</span>
						</div>
					)}
					{!parsing && !!parseError && <Alert severity="error">{parseError}</Alert>}
					{!parsing && !!sheet && (
						<Alert severity="success">
							Planilha carregada: {sheet.rows.length} linhas de risco de {positions} {positions === 1 ? 'cargo' : 'cargos'}.
						</Alert>
					)}
				</div>
				{!!sheet && <RiskMissingPositionsAlert positions={sheet.missingPositions} dates={sheet.sheetDates}/>}
				<div className="acoes">
					<Button
						variant="contained"
						startIcon={starting ? <RiskSpinner size={18}/> : <PlayArrow/>}
						disabled={!canStart || starting}
						onClick={onStart}
					>
						Iniciar
					</Button>
				</div>
			</div>
		</div>
	);
};

export default RiskSelectionStep;
